import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { compatForm } from '@angular/forms/signals/compat';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    getAddressComponent,
    getCityComponent,
    GoogleMapsAutocompleteDirective,
} from '@app/directives/google-maps-autocomplete/google-maps-autocomplete.directive';
import { PatientBaseCaregiverResponse } from '@app/generated-api';
import { LabeledTextComponent } from '@app/v2/components/labeled-text/labeled-text.component';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { CountrySignalService } from '@app/v2/services/country-signal/country-signal.service';
import { computedAllDefinedNotNull, disableFormEffect } from '@app/v2/utils/signal-utils';
import { CardMode } from '@components/card/card.component';
import {
    MdsAutocompleteComponent,
    MdsButtonComponent,
    MdsCheckboxComponent,
    MdsDatepickerComponent,
    MdsDialogActionsComponent,
    MdsDialogComponent,
    MdsDialogContentComponent,
    MdsRadioButton,
    MdsRadioGroupComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { dateFormatPipe } from '../../../../pipes/datepipe';
import { CaregiverFormGroup } from './caregiver-dialog.form';

export type CaregiverDialogData = {
    mode: CardMode;
    defaultPrimary?: boolean;
    isEditMode?: boolean;
    model: PatientBaseCaregiverResponse | null;
    formGroup: CaregiverFormGroup | null;
};

export type CaregiverDialogResult = { result: ModalResult; data: CaregiverDialogData };

@Component({
    selector: 'medpace-caregiver-dialog',
    imports: [
        MdsDialogComponent,
        MdsDialogContentComponent,
        MdsDialogActionsComponent,
        MdsButtonComponent,
        MdsCheckboxComponent,
        MdsTextFieldComponent,
        MdsRadioGroupComponent,
        MdsDatepickerComponent,
        LabeledTextComponent,
        ReactiveFormsModule,
        dateFormatPipe,
        UpperCasePipe,
        MdsAutocompleteComponent,
        GoogleMapsAutocompleteDirective,
    ],
    templateUrl: './caregiver-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaregiverDialogComponent {
    dialogRef: MatDialogRef<CaregiverDialogComponent, CaregiverDialogResult> = inject(
        MatDialogRef<CaregiverDialogComponent, CaregiverDialogResult>
    );
    data = inject<CaregiverDialogData>(MAT_DIALOG_DATA);

    title =
        this.data.model || !!this.data.formGroup?.model().id || this.data.isEditMode
            ? 'Caregiver Profile'
            : 'Add New Caregiver'; // not reactive, should not change
    form = this.data.formGroup ? compatForm(signal(this.data.formGroup.controls)) : null;
    formState = this.data.formGroup ? this.data.formGroup.getRawValue() : null;

    countryService = inject(CountrySignalService);
    countryNames = computedAllDefinedNotNull({ countries: this.countryService.countries.value }, ({ countries }) =>
        countries.map((country) => country.name)
    );

    genderRadioButtons: MdsRadioButton[] = [
        { label: 'Male', value: 'male', name: 'gender-male', id: 'gender-male' },
        { label: 'Female', value: 'female', name: 'gender-female', id: 'gender-female' },
    ];

    addressRadioButtons: MdsRadioButton[] = [
        { label: 'Yes', value: true, name: 'address-yes', id: 'address-yes' },
        { label: 'No', value: false, name: 'address-no', id: 'address-no' },
    ];

    addressSameAsPatient = this.data.formGroup
        ? toSignal(this.data.formGroup.controls.addressSameAsPatient.valueChangesStartWithCurrentValue())
        : null;

    disableAddressForm = disableFormEffect({
        form: () => this.data.formGroup?.controls.address ?? null,
        // params: () => ({ addressSameAsPatient: this.addressSameAsPatient() }),
        disabled: () => (this.addressSameAsPatient ? this.addressSameAsPatient() === true : null),
    });

    constructor() {
        const formGroup = this.data.formGroup;
        if (!formGroup) return;

        if (this.data.defaultPrimary || formGroup.controls.primary.getRawValue()) {
            formGroup.controls.primary.setValue(true);
            formGroup.controls.primary.disable({ persistent: true });
        } else {
            formGroup.controls.primary.enable({ breakPersistence: true });
        }
    }

    onCancel(): void {
        if (this.data.mode === 'edit' && this.data.formGroup && this.formState) {
            this.data.formGroup.reset(this.formState);
        }
        this.dialogRef.close({ result: ModalResult.Cancel, data: this.data });
    }

    onSave(): void {
        if (!this.data.formGroup) throw new Error('Caregiver form group is null');

        if (!this.data.formGroup.valid) {
            this.data.formGroup.markAllAsTouched();
            return;
        }

        if (!this.data.formGroup.model()) throw new Error('Caregiver form model is undefined');
        if (!this.data.formGroup.controls.email.value) throw new Error('Caregiver form email is undefined');
        this.data.formGroup.model()!.email = this.data.formGroup.controls.email.value!; // set email (required in form, must be set here), it will work like id, for CREATE purpose- actual id field is set to 0 in CREATE mode

        this.dialogRef.close({ result: ModalResult.Okay, data: this.data });
    }

    googleMapsAutocompletePlaceChange(place: google.maps.places.Place) {
        const addressComponents = place.addressComponents ?? [];
        const line1 = `${getAddressComponent(
            'street_number',
            addressComponents
        )} ${getAddressComponent('route', addressComponents)}`;
        const city = getCityComponent(place);
        const state = getAddressComponent('administrative_area_level_1', addressComponents);
        const postalCode = getAddressComponent('postal_code', addressComponents);
        const country = getAddressComponent('country', addressComponents);

        this.data.formGroup?.patchValue({
            address: {
                address1: line1,
                city: city,
                state: state,
                zipcode: postalCode,
                country: country,
            },
        } as any);
    }
}
